import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Globe, Calendar, Users, Search, 
  ArrowRight, Loader2, Check, X, ChevronDown,
  AlertCircle, Heart, Plane, Clock, FileText,
  User, Mail, Phone, Download, Star, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Popular regions
const POPULAR_REGIONS = [
  { code: 'EUR', name: 'Europe', type: 'POPULARREGION', icon: '🇪🇺' },
  { code: 'ASI', name: 'Asia', type: 'POPULARREGION', icon: '🌏' },
  { code: 'USC', name: 'USA & Canada', type: 'POPULARREGION', icon: '🇺🇸' },
  { code: 'WW', name: 'Worldwide', type: 'POPULARREGION', icon: '🌍' },
  { code: 'MDE', name: 'Middle East', type: 'POPULARREGION', icon: '🏜️' },
  { code: 'SCH', name: 'Schengen', type: 'POPULARREGION', icon: '🇪🇺' },
];

// Insurance types
const INSURANCE_TYPES = [
  { id: 'standalone', label: 'Standard', description: 'Regular travel insurance' },
  { id: 'student', label: 'Student', description: 'For students studying abroad' },
  { id: 'annual_multi_trip', label: 'Annual Multi-Trip', description: '12-month coverage' },
];

// Plan Card Component
const PlanCard = ({ plan, onSelect, selected }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  const benefits = plan.benefits || [];
  const bannerBenefits = benefits.filter(b => b.bv === 'BANNER').slice(0, 3);
  const allBenefits = benefits.filter(b => b.name);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white/[0.03] backdrop-blur-xl border rounded-2xl overflow-hidden transition-all ${
        selected 
          ? 'border-[#00E5FF] shadow-[0_0_30px_rgba(0,229,255,0.2)]' 
          : 'border-white/10 hover:border-white/20'
      }`}
      data-testid={`plan-card-${plan.pid}`}
    >
      {/* Plan Header */}
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              selected ? 'bg-[#00E5FF]/20' : 'bg-gradient-to-br from-[#3E2A86] to-[#5438B5]'
            }`}>
              <Shield className={`w-7 h-7 ${selected ? 'text-[#00E5FF]' : 'text-white'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-semibold text-lg">{plan.tier || 'Standard'}</h3>
                {plan.tier === 'Titanium' && (
                  <span className="px-2 py-0.5 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-xs font-bold rounded-full">
                    PREMIUM
                  </span>
                )}
              </div>
              <p className="text-white/50 text-sm">{plan.name} Coverage</p>
              <p className="text-white/40 text-xs mt-1">
                Provider: {plan.provider} • {plan.region_name}
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-3xl font-bold text-white">
              ₹{plan.pricing?.total_fare?.toLocaleString() || 0}
            </p>
            <p className="text-white/40 text-xs">Total Premium</p>
          </div>
        </div>
        
        {/* Quick Features */}
        <div className="flex flex-wrap gap-2 mb-4">
          {plan.has_esim && (
            <span className="px-3 py-1 bg-[#00E5FF]/10 text-[#00E5FF] text-xs rounded-full flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Free eSIM
            </span>
          )}
          {plan.has_brb && (
            <span className="px-3 py-1 bg-[#FF007F]/10 text-[#FF007F] text-xs rounded-full flex items-center gap-1">
              <Star className="w-3 h-3" /> Blue Ribbon Bags
            </span>
          )}
          {plan.has_boxx && (
            <span className="px-3 py-1 bg-[#9D4EDD]/10 text-[#9D4EDD] text-xs rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" /> Cyber Protection
            </span>
          )}
        </div>
        
        {/* Key Benefits */}
        {bannerBenefits.length > 0 && (
          <div className="space-y-2 mb-4">
            {bannerBenefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 text-white/70 text-sm">
                <Check className="w-4 h-4 text-[#00E5FF]" />
                <span>{benefit.name}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Partner Logos */}
        {plan.provider_partners && plan.provider_partners.length > 0 && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-white/40 text-xs">Partners:</span>
            <div className="flex gap-2">
              {plan.provider_partners.map((partner, index) => (
                <span key={index} className="px-2 py-1 bg-white/5 rounded text-white/60 text-xs">
                  {partner}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => onSelect(plan)}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              selected
                ? 'bg-[#00E5FF] text-black'
                : 'bg-gradient-to-r from-[#3E2A86] to-[#5438B5] text-white hover:shadow-[0_0_20px_rgba(62,42,134,0.4)]'
            }`}
          >
            {selected ? <Check className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
            {selected ? 'Selected' : 'Select Plan'}
          </button>
          
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
          >
            <ChevronDown className={`w-5 h-5 text-white/60 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 overflow-hidden"
          >
            <div className="p-6 bg-white/[0.02]">
              <h4 className="text-white font-semibold mb-4">All Benefits</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {allBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/80 text-sm">{benefit.name}</p>
                      {benefit.ic && (
                        <p className="text-white/40 text-xs">{benefit.ic}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Traveler Input Component
const TravelerInput = ({ index, traveler, onChange, onRemove, showRemove }) => {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white font-medium">Traveler {index + 1}</h4>
        {showRemove && (
          <button
            onClick={() => onRemove(index)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4 text-white/50" />
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-white/50 text-xs mb-1 block">First Name</label>
          <input
            type="text"
            value={traveler.fn || ''}
            onChange={(e) => onChange(index, 'fn', e.target.value)}
            placeholder="First Name"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Last Name</label>
          <input
            type="text"
            value={traveler.ln || ''}
            onChange={(e) => onChange(index, 'ln', e.target.value)}
            placeholder="Last Name"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Date of Birth</label>
          <input
            type="date"
            value={traveler.dob || ''}
            onChange={(e) => onChange(index, 'dob', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Email</label>
          <input
            type="email"
            value={traveler.eid || ''}
            onChange={(e) => onChange(index, 'eid', e.target.value)}
            placeholder="Email"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Phone</label>
          <input
            type="tel"
            value={traveler.pnum || ''}
            onChange={(e) => onChange(index, 'pnum', e.target.value)}
            placeholder="Phone Number"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Gender</label>
          <select
            value={traveler.gen || ''}
            onChange={(e) => onChange(index, 'gen', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none"
          >
            <option value="" className="bg-[#12101A]">Select</option>
            <option value="M" className="bg-[#12101A]">Male</option>
            <option value="F" className="bg-[#12101A]">Female</option>
          </select>
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Nominee Name</label>
          <input
            type="text"
            value={traveler.nn || ''}
            onChange={(e) => onChange(index, 'nn', e.target.value)}
            placeholder="Nominee Name"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Nominee Relationship</label>
          <select
            value={traveler.nr || ''}
            onChange={(e) => onChange(index, 'nr', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm appearance-none"
          >
            <option value="" className="bg-[#12101A]">Select</option>
            <option value="Spouse" className="bg-[#12101A]">Spouse</option>
            <option value="Parent" className="bg-[#12101A]">Parent</option>
            <option value="Child" className="bg-[#12101A]">Child</option>
            <option value="Sibling" className="bg-[#12101A]">Sibling</option>
            <option value="Other" className="bg-[#12101A]">Other</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// Main InsurancePage Component
export default function InsurancePage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Search Form States
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [insuranceType, setInsuranceType] = useState('standalone');
  const [travelerAges, setTravelerAges] = useState([{ age: 30 }]);
  
  // Search States
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searched, setSearched] = useState(false);
  
  // Booking States
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [travelerDetails, setTravelerDetails] = useState([]);
  
  // Service Status
  const [serviceStatus, setServiceStatus] = useState(null);
  
  // Calculate ages from DOB
  const calculateAge = (dob) => {
    if (!dob) return 30;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  // Check service status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/insurance/status`);
        setServiceStatus(response.data);
      } catch (err) {
        console.error('Service status error:', err);
      }
    };
    checkStatus();
  }, []);
  
  // Initialize traveler details when ages change
  useEffect(() => {
    setTravelerDetails(
      travelerAges.map((t, i) => ({
        id: i + 1,
        fn: user?.name?.split(' ')[0] || '',
        ln: user?.name?.split(' ').slice(1).join(' ') || '',
        dob: '',
        eid: i === 0 ? (user?.email || '') : '',
        pnum: '',
        gen: '',
        nn: '',
        nr: ''
      }))
    );
  }, [travelerAges.length, user]);
  
  const today = new Date().toISOString().split('T')[0];
  const minEndDate = startDate || today;
  
  const addTraveler = () => {
    if (travelerAges.length < 10) {
      setTravelerAges([...travelerAges, { age: 30 }]);
    }
  };
  
  const removeTraveler = (index) => {
    if (travelerAges.length > 1) {
      setTravelerAges(travelerAges.filter((_, i) => i !== index));
    }
  };
  
  const updateTravelerAge = (index, age) => {
    const updated = [...travelerAges];
    updated[index].age = parseInt(age) || 30;
    setTravelerAges(updated);
  };
  
  const searchPlans = async () => {
    if (!selectedRegion) {
      toast.error('Please select a destination region');
      return;
    }
    
    if (!startDate || !endDate) {
      toast.error('Please select travel dates');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    setPlans([]);
    setSelectedPlan(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/insurance/search`, {
        start_date: startDate,
        end_date: endDate,
        regions: [{ rkey: selectedRegion.code, rt: selectedRegion.type }],
        travelers: travelerAges,
        insurance_type: insuranceType
      });
      
      if (response.data.success) {
        const plansData = response.data.plans || [];
        setPlans(plansData);
        
        if (plansData.length > 0) {
          toast.success(`Found ${plansData.length} insurance plans`);
        } else {
          toast.info('No plans available for this region/dates. Try a different combination.');
        }
      } else {
        toast.error(response.data.error || 'Failed to search plans');
      }
    } catch (err) {
      console.error('Plan search error:', err);
      toast.error('Failed to search for plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const reviewSelectedPlan = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan first');
      return;
    }
    
    setBookingLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/insurance/review`, {
        plid: selectedPlan.plid,
        pid: selectedPlan.pid
      });
      
      if (response.data.success) {
        setReviewData(response.data);
        setShowBookingForm(true);
      } else {
        toast.error(response.data.error || 'Failed to review plan');
      }
    } catch (err) {
      console.error('Plan review error:', err);
      toast.error('Failed to review plan. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };
  
  const updateTravelerDetail = (index, field, value) => {
    const updated = [...travelerDetails];
    updated[index][field] = value;
    setTravelerDetails(updated);
  };
  
  const handleBooking = async () => {
    // Validate traveler details
    const validTravelers = travelerDetails.filter(t => 
      t.fn && t.ln && t.dob && t.eid && t.pnum && t.gen && t.nn && t.nr
    );
    
    if (validTravelers.length !== travelerDetails.length) {
      toast.error('Please fill in all traveler details');
      return;
    }
    
    setBookingLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/insurance/booking`, {
        plid: selectedPlan.plid,
        pid: selectedPlan.pid,
        travelers: travelerDetails,
        payment_medium: 'WALLET'
      });
      
      if (response.data.success) {
        toast.success('Insurance booked successfully!');
        navigate('/complete-booking', {
          state: {
            booking_type: 'insurance',
            booking_id: response.data.booking_id,
            policy_number: response.data.policy_number,
            plan: selectedPlan,
            travelers: travelerDetails
          }
        });
      } else {
        // Check for wallet balance issue
        if (response.data.uat_note) {
          toast.error('UAT wallet may not be funded. This is a sandbox limitation.');
        } else {
          toast.error(response.data.error || 'Booking failed');
        }
      }
    } catch (err) {
      console.error('Booking error:', err);
      toast.error('Failed to complete booking. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#09080E]" data-testid="insurance-page">
      <FloatingBackButton />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/80 via-[#09080E]/40 to-[#09080E]" />
        
        <div className="relative z-10 h-full flex flex-col justify-end pb-8 px-4 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p className="text-[#00E5FF] text-sm uppercase tracking-[0.3em] mb-3">
              AIRA TripSafe
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight">
              Travel Insurance
            </h1>
            <p className="text-white/60 text-lg mt-3 max-w-xl">
              Protect your journey with comprehensive coverage from leading insurers
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Search Form */}
      <section className="px-4 md:px-16 -mt-8 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl max-w-5xl mx-auto"
        >
          {/* Region Selection */}
          <div className="mb-6">
            <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">
              Where are you traveling?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {POPULAR_REGIONS.map((region) => (
                <button
                  key={region.code}
                  onClick={() => setSelectedRegion(region)}
                  className={`p-4 rounded-xl text-center transition-all ${
                    selectedRegion?.code === region.code
                      ? 'bg-gradient-to-r from-[#3E2A86] to-[#5438B5] text-white border-2 border-[#00E5FF] shadow-[0_0_20px_rgba(0,229,255,0.3)]'
                      : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`region-${region.code}`}
                >
                  <span className="text-2xl mb-2 block">{region.icon}</span>
                  <span className="text-sm font-medium">{region.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Travel Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">
                Travel Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={today}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white"
                data-testid="insurance-start-date"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">
                Travel End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={minEndDate}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white"
                data-testid="insurance-end-date"
              />
            </div>
          </div>
          
          {/* Insurance Type */}
          <div className="mb-6">
            <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">
              Insurance Type
            </label>
            <div className="flex flex-wrap gap-3">
              {INSURANCE_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setInsuranceType(type.id)}
                  className={`px-5 py-3 rounded-xl transition-all ${
                    insuranceType === type.id
                      ? 'bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-white'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`insurance-type-${type.id}`}
                >
                  <span className="font-medium">{type.label}</span>
                  <span className="text-xs opacity-70 block">{type.description}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Travelers */}
          <div className="mb-6">
            <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">
              Travelers
            </label>
            <div className="space-y-3">
              {travelerAges.map((traveler, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <Users className="w-5 h-5 text-[#00E5FF]" />
                    <span className="text-white">Traveler {index + 1}</span>
                    <div className="flex-1" />
                    <label className="text-white/50 text-sm">Age:</label>
                    <input
                      type="number"
                      value={traveler.age}
                      onChange={(e) => updateTravelerAge(index, e.target.value)}
                      min="0"
                      max="100"
                      className="w-16 bg-white/10 border border-white/10 rounded-lg px-2 py-1 text-white text-center"
                    />
                  </div>
                  {travelerAges.length > 1 && (
                    <button
                      onClick={() => removeTraveler(index)}
                      className="p-3 bg-white/5 hover:bg-red-500/20 border border-white/10 rounded-xl transition-colors"
                    >
                      <X className="w-5 h-5 text-white/50 hover:text-red-400" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {travelerAges.length < 10 && (
              <button
                onClick={addTraveler}
                className="mt-3 text-[#00E5FF] text-sm hover:underline flex items-center gap-1"
              >
                + Add Traveler
              </button>
            )}
          </div>
          
          {/* Search Button */}
          <button
            onClick={searchPlans}
            disabled={loading || !selectedRegion}
            className="w-full bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-black font-semibold py-4 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="search-insurance-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            Find Insurance Plans
          </button>
          
          {/* UAT Notice */}
          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <p className="text-blue-400/80 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              TripJack TripSafe UAT: Real insurance plans with test pricing. Booking uses sandbox wallet.
            </p>
          </div>
        </motion.div>
      </section>
      
      {/* Search Results */}
      <section className="px-4 md:px-16 py-12">
        {searched && (
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-[#00E5FF] animate-spin mb-4" />
                <p className="text-white/60">Searching for insurance plans...</p>
              </div>
            ) : plans.length > 0 ? (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl text-white font-bold mb-2">
                    Available Plans
                  </h2>
                  <p className="text-white/50">
                    {plans.length} plan{plans.length > 1 ? 's' : ''} found for {selectedRegion?.name}
                  </p>
                </div>
                
                <div className="space-y-4">
                  {plans.map((plan, index) => (
                    <PlanCard
                      key={plan.pid || index}
                      plan={plan}
                      onSelect={setSelectedPlan}
                      selected={selectedPlan?.pid === plan.pid}
                    />
                  ))}
                </div>
                
                {/* Proceed to Booking */}
                {selectedPlan && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8"
                  >
                    <button
                      onClick={reviewSelectedPlan}
                      disabled={bookingLoading}
                      className="w-full bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold py-4 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="proceed-insurance-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Proceed to Book <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </motion.div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <Shield className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl text-white/80 mb-2">No Plans Available</h3>
                <p className="text-white/50 max-w-md mx-auto">
                  No insurance plans found for this destination and dates. 
                  Try a different region or travel period.
                </p>
              </div>
            )}
          </div>
        )}
      </section>
      
      {/* Booking Form Modal */}
      <AnimatePresence>
        {showBookingForm && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowBookingForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#12101A] border border-white/10 rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl text-white font-bold">Complete Booking</h2>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>
              
              {/* Plan Summary */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <h3 className="text-white/70 text-sm mb-3">Plan Summary</h3>
                <div className="flex items-center gap-4">
                  <Shield className="w-10 h-10 text-[#00E5FF]" />
                  <div className="flex-1">
                    <p className="text-white font-semibold">
                      {selectedPlan.tier} - {selectedPlan.name}
                    </p>
                    <p className="text-white/50 text-sm">
                      {selectedPlan.region_name} • {startDate} to {endDate}
                    </p>
                    <p className="text-white/50 text-sm">
                      {travelerAges.length} Traveler{travelerAges.length > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#00E5FF]">
                      ₹{selectedPlan.pricing?.total_fare?.toLocaleString() || 0}
                    </p>
                    <p className="text-white/40 text-xs">Total Premium</p>
                  </div>
                </div>
              </div>
              
              {/* Traveler Details */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-4">Traveler Details</h3>
                {travelerDetails.map((traveler, index) => (
                  <TravelerInput
                    key={index}
                    index={index}
                    traveler={traveler}
                    onChange={updateTravelerDetail}
                    onRemove={() => {}}
                    showRemove={false}
                  />
                ))}
              </div>
              
              {/* Book Button */}
              <button
                onClick={handleBooking}
                disabled={bookingLoading}
                className="w-full bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-black font-semibold py-4 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="confirm-insurance-btn"
              >
                {bookingLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Confirm & Pay
                  </>
                )}
              </button>
              
              {/* Note */}
              <p className="text-white/40 text-xs text-center mt-4">
                By proceeding, you agree to the terms and conditions of the insurance policy.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
